
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Contact, Loader2 } from "lucide-react";
import { useSupabase } from "@/hooks/useSupabase";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useSubscription } from "@/hooks/useSubscription";

const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const plansRef = useRef<HTMLDivElement>(null);
  const { isLoggedIn } = useSupabase();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { hasPremiumAccess, subscriptionTier } = useSubscription();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("scrollTo") === "pricing-plans" && plansRef.current) {
      plansRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.search]);

  // Check for a successful session_id in the URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    
    if (sessionId) {
      toast({
        title: "Subscription Active",
        description: "Your subscription has been successfully activated!",
      });
      
      // Clean up the URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      
      // Redirect to account page after a short delay
      setTimeout(() => {
        navigate('/account');
      }, 2000);
    }
  }, [location.search, navigate, toast]);

  const handleSubscribe = async () => {
    if (!isLoggedIn) {
      toast({
        title: "Login Required",
        description: "Please login or create an account to subscribe.",
        variant: "destructive",
      });
      // Redirect to signup page instead of login page
      navigate('/signup', { state: { returnTo: '/pricing' }});
      return;
    }

    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { billingCycle }
      });

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "Failed to start subscription process. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Determine if user already has an active subscription
  const isSubscribed = hasPremiumAccess && subscriptionTier !== 'basic';

  return (
    <Layout>
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-gray-900">Simple, Transparent Pricing</h1>
          <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
            Access premium AI content and features with flexible billing options
          </p>
          
          <div className="mt-8 flex items-center justify-center">
            <span className={`mr-4 ${billingCycle === 'monthly' ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
              Monthly
            </span>
            <Switch
              checked={billingCycle === 'annual'}
              onCheckedChange={(checked) => setBillingCycle(checked ? 'annual' : 'monthly')}
            />
            <span className={`ml-4 ${billingCycle === 'annual' ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
              Annual
              <Badge variant="outline" className="ml-2 bg-green-50 text-green-700 border-green-200">
                Save 20%
              </Badge>
            </span>
          </div>
        </div>
      </div>
      
      <section className="py-16" ref={plansRef}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 md:grid-cols-1 justify-items-center">
            <Card className="w-full max-w-md flex flex-col">
              <CardHeader>
                <CardTitle>AI Content Pro</CardTitle>
                <CardDescription>Premium access to cutting-edge AI insights</CardDescription>
              </CardHeader>
              <CardContent className="flex-grow">
                <div className="mb-4">
                  <span className="text-4xl font-bold">
                    {billingCycle === 'monthly' ? '$0.99' : '$10'}
                  </span>
                  <span className="text-gray-500 ml-2">
                    {billingCycle === 'monthly' ? '/month' : '/year'}
                  </span>
                </div>
                
                <div className="space-y-3 mb-4">
                  <div>First 7 days FREE trial</div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Unlimited access to premium articles</span>
                  </div>
                  <div className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Advanced AI research insights</span>
                  </div>
                  <div className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Weekly newsletter</span>
                  </div>
                  <div className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Early access to new content</span>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                {isSubscribed ? (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate('/account')}
                  >
                    Manage Your Subscription
                  </Button>
                ) : (
                  <Button 
                    variant="default" 
                    className="w-full"
                    onClick={isLoggedIn ? handleSubscribe : () => navigate('/signup', { state: { returnTo: '/pricing' }})}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Start Free Trial'
                    )}
                  </Button>
                )}
              </CardFooter>
            </Card>
          </div>
        </div>
      </section>
      
      <section className="bg-gray-50 py-16" id="faq">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Frequently Asked Questions</h2>
          <div className="grid gap-8 md:grid-cols-2">
            <div>
              <h3 className="text-lg font-medium text-gray-900">What's included in the free plan?</h3>
              <p className="mt-2 text-gray-600">
                The free plan gives you access to our public articles, weekly newsletter, and community discussions. It's perfect for those just starting their AI journey.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-900">Can I switch between plans?</h3>
              <p className="mt-2 text-gray-600">
                Yes, you can upgrade or downgrade your plan at any time. If you upgrade, you'll be charged the prorated amount for the remainder of your billing cycle.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-900">Do you offer discounts for teams?</h3>
              <p className="mt-2 text-gray-600">
                Yes, we offer volume discounts for teams of 5 or more. Please contact our sales team for more information about enterprise pricing.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-900">How do I cancel my subscription?</h3>
              <p className="mt-2 text-gray-600">
                You can cancel your subscription at any time from your account settings. Your access will remain until the end of your current billing period.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-900">Is there a refund policy?</h3>
              <p className="mt-2 text-gray-600">
                We offer a 14-day money-back guarantee for all new subscriptions. If you're not satisfied, contact support within 14 days for a full refund.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-900">What payment methods do you accept?</h3>
              <p className="mt-2 text-gray-600">
                We accept all major credit cards, PayPal, and for enterprise customers, we can arrange invoicing.
              </p>
            </div>
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-gray-600">
              Still have questions? Contact our friendly support team.
            </p>
            <Button variant="outline" className="mt-4" asChild>
              <a href="/contact">
                <Contact className="inline-block mr-2 -mt-1" />
                Contact Support
              </a>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Pricing;
